import { useEffect } from 'react'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import type { Breadcrumb, Event } from '@sentry/react'

import type { OrganizationData, UserData } from '@/types/auth.types'

type SensitivePattern = {
    pattern: RegExp
    replacement: string
}

const SENSITIVE_PATTERNS: SensitivePattern[] = [
    {
        pattern: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g,
        replacement: '[email]',
    },
    {
        pattern: /bearer\s+[\w-]+/gi,
        replacement: 'bearer [redacted]',
    },
    {
        pattern: /emp[_-]?\d{4,}/gi,
        replacement: '[employee-id]',
    },
]

const FEATURE_AREA_RULES: Array<{ pattern: RegExp; area: string }> = [
    { pattern: /^\/?$/, area: 'root' },
    { pattern: /^\/dashboard/, area: 'dashboard' },
    { pattern: /^\/upload/, area: 'uploads' },
    { pattern: /^\/intake/, area: 'hr_intake' },
    { pattern: /^\/people/, area: 'people_management' },
    { pattern: /^\/team/, area: 'team_management' },
    { pattern: /^\/surveys/, area: 'surveys' },
    { pattern: /^\/alerts/, area: 'alerts' },
    { pattern: /^\/reports/, area: 'analytics_reports' },
    { pattern: /^\/kudos/, area: 'recognition' },
    { pattern: /^\/profile/, area: 'profile' },
    { pattern: /^\/notification-settings/, area: 'notification_settings' },
    { pattern: /^\/connect/, area: 'integrations' },
    { pattern: /^\/documentation/, area: 'documentation' },
    { pattern: /^\/auth\//, area: 'authentication' },
    { pattern: /^\/(403|404|error)/, area: 'error_boundary' },
]

let cachedUserRole: string | null = null
let cachedOrganization: Pick<OrganizationData, 'uuid' | 'name'> | null = null

function hasSentryClient() {
    return Boolean(Sentry.getCurrentHub().getClient())
}

function getCurrentLocation() {
    if (typeof window === 'undefined' || !window.location) {
        return {
            pathname: undefined,
            search: undefined,
        }
    }

    const { pathname, search } = window.location
    return {
        pathname,
        search,
    }
}

export function getCurrentFeatureArea(pathnameOverride?: string): string {
    const pathname = pathnameOverride ?? getCurrentLocation().pathname ?? ''

    const rule = FEATURE_AREA_RULES.find((candidate) => candidate.pattern.test(pathname))
    return rule ? rule.area : 'unknown'
}

export function getCurrentFeature(pathnameOverride?: string): string {
    const { pathname, search } = pathnameOverride ? { pathname: pathnameOverride, search: '' } : getCurrentLocation()

    if (!pathname) {
        return 'unknown'
    }

    return `${pathname}${search ?? ''}`
}

function sanitizeString(value: string, patterns: SensitivePattern[]): string {
    return patterns.reduce((acc, { pattern, replacement }) => acc.replace(pattern, replacement), value)
}

export function scrubEventRecursively<T>(input: T, patterns: SensitivePattern[] = SENSITIVE_PATTERNS): T {
    const visited = new WeakSet<object>()

    const scrub = (value: unknown): unknown => {
        if (typeof value === 'string') {
            return sanitizeString(value, patterns)
        }

        if (Array.isArray(value)) {
            for (let index = 0; index < value.length; index += 1) {
                value[index] = scrub(value[index])
            }
            return value
        }

        if (value && typeof value === 'object') {
            if (visited.has(value as object)) {
                return value
            }
            visited.add(value as object)
            const record = value as Record<string, unknown>
            Object.keys(record).forEach((key) => {
                record[key] = scrub(record[key])
            })
            return record
        }

        return value
    }

    return scrub(input) as T
}

export function scrubOptimoSensitiveData<T extends Event>(event: T): T {
    if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs
            .filter((crumb: Breadcrumb) => {
                if (!crumb.message || typeof crumb.message !== 'string') {
                    return true
                }
                return !crumb.message.toLowerCase().includes('fieldmapping')
            })
            .map((crumb: Breadcrumb) => {
                if (crumb.message && typeof crumb.message === 'string') {
                    return {
                        ...crumb,
                        message: sanitizeString(crumb.message, SENSITIVE_PATTERNS),
                    }
                }
                return crumb
            })
    }

    return scrubEventRecursively(event) as T
}

export function enhanceWithOptimoContext<T extends Event>(event: T): T {
    const featureArea = getCurrentFeatureArea()
    const currentFeature = getCurrentFeature()

    event.tags = {
        ...event.tags,
        environment: import.meta.env.VITE_ENVIRONMENT ?? 'development',
        feature_area: featureArea,
        user_role: cachedUserRole ?? event.tags?.user_role ?? 'unknown',
        organization_id: cachedOrganization?.uuid ?? event.tags?.organization_id,
    }

    event.contexts = {
        ...event.contexts,
        optimo_context: {
            ...(event.contexts?.optimo_context as Record<string, unknown> | undefined),
            company_uuid: cachedOrganization?.uuid,
            company_name: cachedOrganization?.name,
            current_feature: currentFeature,
        },
    }

    return scrubOptimoSensitiveData(event) as T
}

export function initSentry(): void {
    if (!import.meta.env.VITE_SENTRY_DSN) {
        return
    }

    if (hasSentryClient()) {
        return
    }

    // Distributed tracing: Propagate Sentry tracing headers to the Optimo API.
    //
    // Why: This enables Sentry to link frontend navigations/spans with backend
    // transactions, so traces show the full path FE → BE. Our backend allows
    // the necessary headers ("sentry-trace" and "baggage") in CORS.
    //
    // How: Compute the API origin from VITE_API_URL and pass it to
    // `tracePropagationTargets`, which tells the Sentry browser tracing
    // integration which cross-origins should receive tracing headers.
    // If VITE_API_URL is unset or unparsable, we skip configuration safely.
    const apiUrl = import.meta.env.VITE_API_URL
    let apiOrigin: string | undefined
    try {
        if (apiUrl) {
            apiOrigin = new URL(apiUrl).origin
        }
    } catch {
        apiOrigin = undefined
    }

    Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_ENVIRONMENT ?? 'development',
        release: `optimo-frontend@${import.meta.env.VITE_APP_VERSION ?? 'dev'}`,
        tracesSampleRate: import.meta.env.VITE_ENVIRONMENT === 'production' ? 0.02 : 0.1,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        integrations: [
            Sentry.replayIntegration({
                maskAllText: import.meta.env.VITE_ENVIRONMENT === 'production',
                blockAllMedia: true,
            }),
            Sentry.reactRouterV7BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
                // Propagate tracing headers to the API origin so backend traces
                // can be linked with frontend spans in Sentry.
                ...(apiOrigin ? { tracePropagationTargets: [apiOrigin] } : {}),
            }),
        ],
        beforeSend: (event, _hint) => enhanceWithOptimoContext(event),
    })
}

export function setOptimoUserContext(user: UserData | null, organization: OrganizationData | null): void {
    cachedUserRole = user?.role?.role ?? null
    cachedOrganization = organization ? { uuid: organization.uuid, name: organization.name } : null

    if (!hasSentryClient()) {
        return
    }

    if (!user) {
        Sentry.setUser(null)
        Sentry.setTag('user_role', 'unauthenticated')
        Sentry.setTag('organization_id', 'none')
        Sentry.setContext('optimo_context', {
            company_uuid: null,
            company_name: null,
            current_feature: getCurrentFeature(),
        })
        return
    }

    Sentry.setUser({
        id: user.uuid,
        email: user.email,
        username: user.full_name,
    })

    const role = user.role?.role ?? 'unknown'
    const orgUuid = organization?.uuid ?? null
    const orgName = organization?.name ?? null

    Sentry.setTag('user_role', role)
    Sentry.setTag('organization_id', orgUuid ?? 'none')

    Sentry.setContext('optimo_context', {
        company_uuid: orgUuid,
        company_name: orgName,
        current_feature: getCurrentFeature(),
    })
}

export function clearOptimoUserContext(): void {
    cachedUserRole = null
    cachedOrganization = null

    if (!hasSentryClient()) {
        return
    }

    Sentry.setUser(null)
    Sentry.setTag('user_role', 'unauthenticated')
    Sentry.setTag('organization_id', 'none')
    Sentry.setContext('optimo_context', {
        company_uuid: null,
        company_name: null,
        current_feature: getCurrentFeature(),
    })
}

export function getCurrentUserRole(): string | null {
    return cachedUserRole
}

export function profileCriticalOperation<T>(operationType: string, callback: () => Promise<T> | T): Promise<T> | T {
    if (!hasSentryClient()) {
        return callback()
    }

    return Sentry.startSpan({ op: 'optimo.critical', name: operationType }, (span) => {
        const userRole = getCurrentUserRole()
        if (userRole) {
            span.setAttribute('user_role', userRole)
        }
        span.setAttribute('operation_type', operationType)

        const result = callback()
        if (result && typeof (result as Promise<T>).then === 'function') {
            return (result as Promise<T>).catch((error) => {
                if (span && typeof span.setStatus === 'function') {
                    span.setStatus({ code: 2, message: error instanceof Error ? error.message : 'unknown' })
                }
                throw error
            })
        }

        return result
    })
}
